import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    DeliveryDetailRequest,
    DeliveryDetailItem,
} from '../types/deliveryDetail';
import { getDeliveryDetail } from '../api/deliveryApi';

export const useDeliveryHistoryDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [detailList, setDetailList] = useState<DeliveryDetailItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [totalDeliveryAmt, setTotalDeliveryAmt] = useState<number | null>(null);

    const slotType = useMemo<string | null>(() => {
        const search = new URLSearchParams(location.search);
        return search.get('slotType');
    }, [location.search]);

    const slotName = useMemo<string | null>(() => {
        const search = new URLSearchParams(location.search);
        return search.get('slotName');
    }, [location.search]);

    const getSlotTitle = useCallback((slotType: string | null, name: string | null): string => {
        if (name) return name;
        const slotTitleMap: Record<string, string> = {
            'morning-lunch-peak': '아침/점심 피크',
            'afternoon-non-peak': '오후 논피크',
            'evening-peak': '저녁 피크',
            'night-peak': '야간/심야',
        };
        return slotTitleMap[slotType || ''] || '배달이력 상세 내역';
    }, []);

    // slotType을 peakTimeType으로 변환
    const convertSlotTypeToPeakTimeType = useCallback((slotType: string | null): string | undefined => {
        const peakTimeTypeMap: Record<string, string> = {
            'morning-lunch-peak': 'MORNING_LUNCH',
            'afternoon-non-peak': 'AFTERNOON_NON',
            'evening-peak': 'EVENING',
            'night-peak': 'NIGHT_MIDNIGHT',
        };
        return slotType ? peakTimeTypeMap[slotType] : undefined;
    }, []);

    const detailParams = useMemo<DeliveryDetailRequest | null>(() => {
        const search = new URLSearchParams(location.search);
        const state = location.state as { seqNo?: number; fromDate?: string; toDate?: string } | null;
        
        const seqNo = search.get('seqNo') 
            ? Number(search.get('seqNo'))
            : state?.seqNo ?? undefined;
        
        const fromDate = search.get('fromDate') || state?.fromDate;
        const toDate = search.get('toDate') || state?.toDate;
        const peakTimeType = convertSlotTypeToPeakTimeType(slotType);

        // slotType이 있는 경우(슬롯 클릭으로 진입): peakTimeType 매핑이 필수
        if (slotType) {
            if (!peakTimeType || !fromDate) return null;
            return {
                peakTimeType,
                fromDate,
                toDate,
                deliveryDate: fromDate,
            };
        }

        // 기존 형식 (seqNo 또는 날짜 범위)
        if (!seqNo && !fromDate && !toDate) {
            return null;
        }

        return {
            seqNo,
            fromDate,
            toDate,
        };
    }, [location.search, location.state, slotType, convertSlotTypeToPeakTimeType]);

    // API 호출
    useEffect(() => {
        if (!detailParams) {
            setError('상세 조회에 필요한 정보가 없습니다.');
            setDetailList([]);
            setTotalDeliveryAmt(null);
            return;
        }

        let isMounted = true;

        const fetchDetail = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const response = await getDeliveryDetail(detailParams);

                if (!isMounted) return;

                if (response.success) {
                    // totalDeliveryAmt 저장
                    if (response.totalDeliveryAmt !== undefined) {
                        setTotalDeliveryAmt(response.totalDeliveryAmt);
                    } else {
                        setTotalDeliveryAmt(null);
                    }
                    
                    // deliveryDetails가 있으면 매핑, 없으면 기존 detailList 사용
                    let mappedList: DeliveryDetailItem[] = [];
                    
                    if (response.deliveryDetails && response.deliveryDetails.length > 0) {
                        mappedList = response.deliveryDetails.map((item) => ({
                            deliveryDate: item.deliveryDate,
                            deliveryStatus: item.deliveryStatus,
                            deliveryFee: item.deliveryAmt,
                            faultReason: item.riderFaultYn === 'Y' ? (item.faultReason || '귀책') : null,
                        }));
                    } else if (response.detailList) {
                        mappedList = response.detailList;
                    }
                    
                    setDetailList(mappedList);
                } else {
                    setError(response.message || '상세 정보를 불러오지 못했습니다.');
                    setDetailList([]);
                    setTotalDeliveryAmt(null);
                }
            } catch (fetchError) {
                if (!isMounted) return;
                setError('상세 정보를 불러오는 중 오류가 발생했습니다.');
                setDetailList([]);
                setTotalDeliveryAmt(null);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchDetail();

        return () => {
            isMounted = false;
        };
    }, [detailParams]);

    const formatDate = useCallback((dateString: string) => {
        if (!dateString) return '-';
        // YYYYMMDD 형식을 YYYY-MM-DD로 변환
        if (dateString.length === 8 && !dateString.includes('-')) {
            return `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`;
        }
        return dateString;
    }, []);

    const formatAmount = useCallback((amount: number | null) => {
        if (amount === null || amount === undefined) return '-';
        return `${amount.toLocaleString()} 원`;
    }, []);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const dateRangeText = useMemo<string | null>(() => {
        const { fromDate, toDate } = detailParams || {};
        
        if (!fromDate && !toDate) {
            return null;
        }

        const formattedFromDate = fromDate ? formatDate(fromDate) : null;
        const formattedToDate = toDate ? formatDate(toDate) : null;

        if (formattedFromDate && formattedToDate) {
            if (formattedFromDate === formattedToDate) {
                return formattedFromDate;
            }
            return `${formattedFromDate} ~ ${formattedToDate}`;
        }

        if (formattedFromDate) {
            return formattedFromDate;
        }

        if (formattedToDate) {
            return formattedToDate;
        }

        return null;
    }, [detailParams, formatDate]);

    return {
        detailList,
        isLoading,
        error,
        formatDate,
        formatAmount,
        handleBack,
        slotTitle: getSlotTitle(slotType, slotName),
        dateRangeText,
        totalDeliveryAmt,
    };
};

